import { getToolName, isToolUIPart, type UIMessage } from "ai";

export type SettleWriteToolOutcome = "approved" | "declined";

function isPendingWriteToolState(
  part: Extract<UIMessage["parts"][number], { toolCallId: string }>
): boolean {
  return (
    part.state === "input-available" ||
    part.state === "approval-requested" ||
    (part.state === "input-streaming" && Boolean(part.input))
  );
}

/**
 * Mark a pending write tool part as settled so client history no longer looks approval-pending.
 */
export function settleWriteToolPartInMessages({
  messages,
  toolCallId,
  isWriteToolName,
  outcome,
  declineReason = "User rejected the proposal.",
}: {
  messages: UIMessage[];
  toolCallId: string;
  isWriteToolName: (toolName: string) => boolean;
  outcome: SettleWriteToolOutcome;
  declineReason?: string;
}): UIMessage[] {
  if (!toolCallId) return messages;

  let changed = false;
  const next = messages.map((message) => {
    if (message.role !== "assistant") return message;
    let messageChanged = false;
    const parts = message.parts.map((part) => {
      if (!isToolUIPart(part)) return part;
      if (part.toolCallId !== toolCallId) return part;
      if (!isWriteToolName(getToolName(part))) return part;
      if (!isPendingWriteToolState(part) && part.state === "output-available") {
        return part;
      }
      messageChanged = true;
      if (outcome === "declined") {
        return {
          ...part,
          state: "output-available" as const,
          output: { success: false, error: declineReason },
        } as typeof part;
      }
      return {
        ...part,
        state: "output-available" as const,
        output: part.output ?? { success: true },
      } as typeof part;
    });
    if (!messageChanged) return message;
    changed = true;
    return { ...message, parts };
  });

  return changed ? next : messages;
}
